import React, { useEffect, useState } from 'react';
import { Check, ChevronRight, Clock, FolderKanban, FolderPlus, Loader2, MessageSquare, Plus, RefreshCw, Sparkles, UserPlus, Users, Video, X } from 'lucide-react';
import { apiService } from '../../services/api';
import { NewPointVisibility, PointVisibility, defaultVisibility } from './PointVisibility';

// Réunions du jour, chacune avec ses points à aborder sous les yeux : cocher, ajouter, rattacher la réunion à un
// projet (retenu pour toute la série) ; « Muse » ouvre la préparation complète.

const VISIBLE_POINTS = 5;

let projectOptions = null; // partagé entre les réunions, chargé une fois
const loadProjectOptions = async () => {
  if (!projectOptions) projectOptions = apiService.get('/projects/options?statuses=in_progress').catch(() => []);
  return projectOptions;
};

export const pointTarget = (meeting) => ({
  ...(meeting.project_id ? { project_id: meeting.project_id } : meeting.client_id ? { client_id: meeting.client_id } : {}),
  ...(meeting.series_id ? { series_id: meeting.series_id } : {}),
});

const ProjectLink = ({ meeting, onLinked }) => {
  const [choosing, setChoosing] = useState(false);
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (choosing) loadProjectOptions().then(setOptions);
  }, [choosing]);

  const choose = async (value) => {
    setSaving(true);
    try {
      await apiService.put('/home/event-links', { series_id: meeting.series_id, project_id: value === 'none' ? null : Number(value) });
      setChoosing(false);
      onLinked();
    } finally {
      setSaving(false);
    }
  };

  if (choosing) {
    return (
      <select autoFocus defaultValue="" disabled={saving} onChange={(event) => event.target.value && choose(event.target.value)}
        onBlur={() => setChoosing(false)}
        className="max-w-[220px] rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-xs outline-none focus:ring-2 focus:ring-indigo-100">
        <option value="">Projet de cette réunion…</option>
        <option value="none">Aucun : réunion interne</option>
        {options.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
      </select>
    );
  }
  return meeting.project_name ? (
    <button type="button" onClick={() => setChoosing(true)} title="Changer le projet de cette réunion"
      className="flex max-w-[220px] items-center gap-1 truncate rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200">
      <FolderKanban size={11} className="shrink-0" /> <span className="truncate">{meeting.project_name}</span>
    </button>
  ) : (
    <button type="button" onClick={() => setChoosing(true)} className="text-[11px] font-medium text-indigo-600 hover:underline">
      {meeting.client_name ? `${meeting.client_name} · choisir le projet` : 'Rattacher à un projet'}
    </button>
  );
};

// Réunion sans projet que Nexus voit comme une nouvelle affaire : créer le projet (en proposition) et l'y rattacher.
const NewProject = ({ meeting, onLinked }) => {
  const [state, setState] = useState('idle'); // idle | busy | hidden
  const [error, setError] = useState(null);
  if (!meeting.new_project || meeting.project_id || state === 'hidden') return null;
  const run = async (path) => {
    setState('busy');
    setError(null);
    try {
      await apiService.post(`/home/project-suggestions/${path}`, { series_id: meeting.series_id });
      setState('hidden');
      if (path === 'create') onLinked();
    } catch (err) {
      setError(err.message);
      setState('idle');
    }
  };
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 rounded-lg bg-indigo-50/70 px-2 py-1 text-xs text-indigo-900">
      <FolderPlus size={12} className="shrink-0" />
      <span className="min-w-0 flex-1">Nouveau projet ? <strong>{meeting.new_project.name}</strong></span>
      <button type="button" disabled={state === 'busy'} onClick={() => run('create')}
        className="flex items-center gap-1 rounded-md bg-indigo-600 px-2 py-0.5 font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
        {state === 'busy' && <Loader2 size={11} className="animate-spin" />}Créer
      </button>
      <button type="button" disabled={state === 'busy'} onClick={() => run('dismiss')} title="Ce n'est pas un nouveau projet"
        className="rounded-md p-0.5 text-indigo-400 hover:bg-indigo-100 hover:text-indigo-700">
        <X size={12} />
      </button>
      {error && <span className="w-full text-rose-600">{error}</span>}
    </div>
  );
};

// Note de préparation de Muse (brief du matin, ou analyse dès qu'une réunion apparaît dans l'agenda) ; ↻ la refait.
const MuseNote = ({ meeting }) => {
  const [note, setNote] = useState(meeting.note);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setNote(meeting.note); }, [meeting.note]);
  const refresh = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiService.post(`/home/meetings/${encodeURIComponent(meeting.event_id)}/analyze`, {});
      setNote(result.note);
      if (!result.note) setError('Nexus n\'a rien trouvé de concret à préparer.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-600">
      <Sparkles size={11} className="mt-0.5 shrink-0 text-indigo-400" />
      <p className="min-w-0 flex-1">
        {busy ? <span className="text-slate-400">Nexus analyse la réunion…</span>
          : note || <span className="text-slate-400">{error || 'Pas encore de note de Nexus.'}</span>}
      </p>
      <button type="button" onClick={refresh} disabled={busy} title="Relancer l'analyse de Nexus"
        className="shrink-0 rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-indigo-600 disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
      </button>
    </div>
  );
};

// Invités pas encore dans le projet : contact du client (avec son rôle) ou collègue (dans un rôle du projet).
const AttendeeRow = ({ meeting, person, onDone }) => {
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const run = async (action) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      onDone(person);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const add = () => run(() => (person.kind === 'member'
    ? apiService.post('/home/project-members', { project_id: meeting.project_id, employee_id: person.employee_id, role_id: Number(role) })
    : apiService.post('/home/project-contacts', { project_id: meeting.project_id, email: person.email, name: person.name, role: role.trim() || null })));
  const dismiss = () => run(() => apiService.post('/home/attendee-dismissals', { project_id: meeting.project_id, email: person.email }));
  return (
    <li className="py-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate text-[13px] text-slate-700" title={person.email}>
          {person.name} <span className="text-[11px] text-slate-400">{person.kind === 'member' ? 'Innowide' : 'externe'}</span>
        </span>
        {person.kind === 'member' ? (
          <select value={role} onChange={(event) => setRole(event.target.value)} disabled={saving}
            className="max-w-[150px] rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-xs outline-none focus:ring-2 focus:ring-indigo-100">
            <option value="">Rôle…</option>
            {meeting.role_options.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
        ) : (
          <input value={role} onChange={(event) => setRole(event.target.value)} disabled={saving} placeholder="Son rôle (facultatif)"
            onKeyDown={(event) => { if (event.key === 'Enter') add(); }}
            className="w-[150px] rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-xs outline-none focus:ring-2 focus:ring-indigo-100" />
        )}
        <button type="button" onClick={add} disabled={saving || (person.kind === 'member' && !role)}
          className="rounded-md bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-40">
          {saving ? '…' : 'Ajouter'}
        </button>
        <button type="button" onClick={dismiss} disabled={saving} title="Ne plus proposer pour ce projet"
          className="rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-500"><X size={13} /></button>
      </div>
      {error && <p className="text-[11px] text-rose-600">{error}</p>}
    </li>
  );
};

const AttendeesToAdd = ({ meeting }) => {
  const [people, setPeople] = useState(meeting.attendees_to_add || []);
  useEffect(() => { setPeople(meeting.attendees_to_add || []); }, [meeting.attendees_to_add]);
  if (!people.length) return null;
  return (
    <div className="mt-2 rounded-lg border border-dashed border-indigo-200 bg-indigo-50/30 px-2.5 py-1.5">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700">
        <UserPlus size={12} /> Pas encore dans {meeting.project_name || 'le projet'}
      </p>
      <ul className="divide-y divide-indigo-100/60">
        {people.map((person) => (
          <AttendeeRow key={person.email} meeting={meeting} person={person}
            onDone={(done) => setPeople((prev) => prev.filter((p) => p.email !== done.email))} />
        ))}
      </ul>
    </div>
  );
};

// Deux invitations pour la même réunion : fusionnées (« Séparer » si ce n'était pas la même) ; deux réunions à la même
// heure : « Même réunion ? ».
const Twins = ({ meeting, onLinked }) => {
  const [busy, setBusy] = useState(false);
  const decide = async (other, same) => {
    setBusy(true);
    try {
      await apiService.post('/home/event-pairs', { first_id: meeting.event_id, second_id: other.id, same });
      onLinked();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {(meeting.merged || []).map((other) => (
        <p key={other.id} className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
          <span>Aussi reçue comme « {other.title} » : même réunion, une seule fiche.</span>
          <button type="button" disabled={busy} onClick={() => decide(other, false)} className="text-slate-500 underline-offset-2 hover:underline">Séparer</button>
        </p>
      ))}
      {(meeting.overlaps || []).map((other) => (
        <div key={other.id} className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-900">
          <span>En même temps que « {other.title} »{other.time ? ` (${other.time})` : ''} : même réunion ?</span>
          <button type="button" disabled={busy} onClick={() => decide(other, true)} className="rounded-md bg-white px-2 py-0.5 font-medium ring-1 ring-amber-200 hover:bg-amber-100">Oui, fusionner</button>
          <button type="button" disabled={busy} onClick={() => decide(other, false)} className="rounded-md px-2 py-0.5 hover:bg-amber-100">Non, deux réunions</button>
        </div>
      ))}
    </>
  );
};

const MeetingItem = ({ meeting, now, onPrepare, onLinked, onCountChange, compact = false }) => {
  const [points, setPoints] = useState(meeting.points || []);
  // Réunion avec des personnes extérieures : points publics par défaut ; réunion interne : privés.
  const [visibility, setVisibility] = useState(() => defaultVisibility((meeting.external || []).length > 0));
  const [unfolded, setUnfolded] = useState(!compact); // réunions du lendemain : repliées, un clic les ouvre
  const [draft, setDraft] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setPoints(meeting.points || []); }, [meeting.points]);

  const replace = (next) => {
    setPoints(next);
    onCountChange(meeting.event_id, next.filter((p) => p.status === 'open').length);
  };

  const toggle = async (point) => {
    const status = point.status === 'open' ? 'discussed' : 'open';
    const previous = points;
    replace(points.map((p) => (p.id === point.id ? { ...p, status } : p)));
    try {
      await apiService.patch(`/talking-points/${point.id}`, status === 'discussed'
        ? { status, discussed_ref: `calendar:${meeting.event_id}` } : { status });
    } catch (err) {
      replace(previous);
      setError(err.message);
    }
  };

  const add = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    try {
      const created = await apiService.post('/talking-points', { text, visibility, ...pointTarget(meeting) });
      replace([...points, created]);
    } catch (err) {
      setDraft(text);
      setError(err.message);
    }
  };

  const share = async (point, value) => {
    const previous = points;
    replace(points.map((p) => (p.id === point.id ? { ...p, visibility: value } : p)));
    try {
      await apiService.patch(`/talking-points/${point.id}`, { visibility: value });
    } catch (err) {
      replace(previous);
      setError(err.message);
    }
  };

  const start = new Date(meeting.start);
  const end = meeting.end ? new Date(meeting.end) : null;
  const live = start <= now && (!end || end > now);
  const past = end && end <= now;
  const open = points.filter((p) => p.status === 'open');
  const done = points.filter((p) => p.status !== 'open');
  const shown = expanded ? open : open.slice(0, VISIBLE_POINTS);

  return (
    <li className={`rounded-xl border px-3 py-2.5 ${live ? 'border-indigo-200 bg-indigo-50/40' : 'border-slate-100'} ${past ? 'opacity-70' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 shrink-0 pt-0.5 text-right">
          <p className="text-sm font-semibold tabular-nums text-slate-800">{meeting.time}</p>
          {meeting.end_time && <p className="text-[11px] tabular-nums text-slate-400">{meeting.end_time}</p>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            {compact ? (
              <button type="button" onClick={() => setUnfolded((v) => !v)} title={unfolded ? 'Replier' : 'Ouvrir la préparation'}
                className="flex min-w-0 flex-1 items-start gap-1 text-left text-sm font-semibold text-slate-900 hover:text-indigo-700">
                <span className="min-w-0 flex-1">{meeting.title}</span>
                {!unfolded && open.length > 0 && <span className="shrink-0 rounded-full bg-indigo-50 px-1.5 text-[11px] font-medium text-indigo-700">{open.length} point{open.length > 1 ? 's' : ''}</span>}
                <ChevronRight size={14} className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${unfolded ? 'rotate-90' : ''}`} />
              </button>
            ) : (
              <p className="min-w-0 flex-1 text-sm font-semibold text-slate-900" title={meeting.title}>{meeting.title}</p>
            )}
            {live && <span className="shrink-0 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold text-white">En cours</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            {meeting.external?.length > 0 && <span className="flex items-center gap-1 truncate"><Users size={11} /> {meeting.external.join(', ')}</span>}
            <ProjectLink meeting={meeting} onLinked={onLinked} />
          </div>
          <Twins meeting={meeting} onLinked={onLinked} />
          {!past && <MuseNote meeting={meeting} />}
          <NewProject meeting={meeting} onLinked={onLinked} />

          {unfolded && (<>
          <ul className="mt-2 space-y-0.5">
            {shown.map((point) => (
              <li key={point.id} className="group flex items-start gap-2">
                <button type="button" onClick={() => toggle(point)} title="Abordé"
                  className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-500">
                  <Check size={11} />
                </button>
                <span className="min-w-0 flex-1 text-[13px] leading-snug text-slate-700">{point.text}</span>
                <PointVisibility point={point} mine={point.mine} onChange={(value) => share(point, value)} />
              </li>
            ))}
            {open.length > VISIBLE_POINTS && (
              <li>
                <button type="button" onClick={() => setExpanded((v) => !v)} className="pl-6 text-[11px] font-medium text-slate-500 hover:text-slate-700">
                  {expanded ? 'Moins' : `+ ${open.length - VISIBLE_POINTS} autres`}
                </button>
              </li>
            )}
            {done.map((point) => (
              <li key={point.id} className="flex items-start gap-2">
                <button type="button" onClick={() => toggle(point)} title="Remettre à aborder"
                  className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-emerald-500 bg-emerald-500 text-white">
                  <Check size={11} />
                </button>
                <span className="text-[13px] leading-snug text-slate-400 line-through decoration-slate-300">{point.text}</span>
              </li>
            ))}
          </ul>

          <form onSubmit={add} className="mt-1.5 flex items-center gap-2">
            <Plus size={14} className="shrink-0 text-slate-300" />
            <input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Point à aborder…"
              className="min-w-0 flex-1 border-0 border-b border-transparent bg-transparent py-0.5 text-[13px] outline-none placeholder:text-slate-400 focus:border-indigo-200" />
            {draft.trim() && <NewPointVisibility value={visibility} onChange={setVisibility} />}
          </form>
          {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
          {!past && <AttendeesToAdd meeting={meeting} />}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => onPrepare(meeting)}
              className="flex items-center gap-1 rounded-lg border border-indigo-200 px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50">
              <Sparkles size={12} /> Préparer avec Nexus <ChevronRight size={12} />
            </button>
            {meeting.link && !past && (
              <a href={meeting.link} target="_blank" rel="noreferrer"
                className={`flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold ${live ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}>
                <Video size={12} /> Rejoindre
              </a>
            )}
          </div>
          </>)}
        </div>
      </div>
    </li>
  );
};

const TodayMeetings = ({ meetings, nextDay, connected, onPrepare, onLinked, onCountChange, navigate }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  const upcoming = meetings.filter((m) => !m.end || new Date(m.end) > now);
  const finished = meetings.length - upcoming.length;
  const [showFinished, setShowFinished] = useState(false);
  const list = showFinished ? meetings : upcoming;

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <MessageSquare size={16} className="text-slate-400" /> Réunions du jour
          {meetings.length > 0 && <span className="rounded-full bg-slate-100 px-2 text-xs font-medium text-slate-500">{meetings.length}</span>}
        </h2>
        {finished > 0 && (
          <button type="button" onClick={() => setShowFinished((v) => !v)} className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
            <Clock size={12} /> {showFinished ? 'Masquer les terminées' : `${finished} terminée${finished > 1 ? 's' : ''}`}
          </button>
        )}
      </div>
      {!connected ? (
        <p className="text-sm text-slate-500">
          Relie ton agenda Google pour retrouver ici tes réunions et leurs points.{' '}
          <button type="button" onClick={() => navigate('/ai-preferences')} className="font-semibold text-indigo-600 hover:underline">Relier</button>
        </p>
      ) : list.length === 0 ? (
        <p className="text-sm text-slate-400">{meetings.length ? 'Plus de réunion aujourd\'hui.' : 'Aucune réunion aujourd\'hui.'}</p>
      ) : (
        <ul className="space-y-2.5">
          {list.map((meeting) => (
            <MeetingItem key={meeting.event_id} meeting={meeting} now={now} onPrepare={onPrepare} onLinked={onLinked}
              onCountChange={onCountChange} />
          ))}
        </ul>
      )}
      {connected && nextDay?.meetings?.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            À préparer pour {nextDay.label}
          </p>
          <ul className="space-y-2.5">
            {nextDay.meetings.map((meeting) => (
              <MeetingItem key={meeting.event_id} meeting={meeting} now={now} onPrepare={onPrepare} onLinked={onLinked}
                onCountChange={onCountChange} compact />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export const LoadingCard = ({ label }) => (
  <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> {label}</p>
);

export default TodayMeetings;
